/**
 * "Do I have to save this?" — no, and the other screen should know.
 *
 * Grid moves are written to the server the moment they land, so there is
 * nothing to press Save on. The team summary, though, is its own fetch on its
 * own page, so it used to keep showing the figures it loaded with until the
 * operator reloaded the browser.
 *
 * The grid announces a change here; anything reading the same event refetches.
 * A BroadcastChannel covers a second tab, and the storage write covers browsers
 * without one — both land in the same handler.
 */

const CHANNEL = 'rle-schedule-changed';

const channel = (): BroadcastChannel | null => {
  try {
    return typeof BroadcastChannel === 'function' ? new BroadcastChannel(CHANNEL) : null;
  } catch { return null; }
};

/** Tell every open screen that this event's schedule just changed. */
export function announceScheduleChange(eventId: string | null | undefined) {
  if (!eventId) return;
  const payload = { eventId, at: Date.now() };
  const ch = channel();
  try { ch?.postMessage(payload); } finally { ch?.close(); }
  try { localStorage.setItem(CHANNEL, JSON.stringify(payload)); } catch { /* private mode */ }
}

/** Run `onChange` whenever this event's schedule changes elsewhere. */
export function subscribeToScheduleChanges(
  eventId: string | null | undefined,
  onChange: () => void,
): () => void {
  if (!eventId) return () => {};

  const matches = (value: any) => String(value?.eventId ?? '') === String(eventId);

  const ch = channel();
  const onMessage = (e: MessageEvent) => { if (matches(e.data)) onChange(); };
  ch?.addEventListener('message', onMessage);

  const onStorage = (e: StorageEvent) => {
    if (e.key !== CHANNEL || !e.newValue) return;
    try { if (matches(JSON.parse(e.newValue))) onChange(); } catch { /* ignore */ }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    ch?.removeEventListener('message', onMessage);
    ch?.close();
    window.removeEventListener('storage', onStorage);
  };
}
